import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ImageData, ImagesService } from '@libraries/assets';
import { UploadPolicy } from '@libraries/secure-uploader-client';
import { getUploadPolicy } from '@libraries/ui-state';
import { Store, select } from '@ngrx/store';
import { BehaviorSubject, Observable, Subscription, animationFrameScheduler, of } from 'rxjs';
import { distinctUntilKeyChanged, filter, map, switchMap, takeWhile } from 'rxjs/operators';

export interface ImageMeta {
  title: string;
  image: string;
  translate?: boolean;
}

@Component({
  selector: 'whole-view-picture',
  templateUrl: './whole-view-picture.component.html',
  styleUrls: ['./whole-view-picture.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class WholeViewPictureComponent implements OnInit, OnDestroy {
  // FIRST IMAGE
  firstTitle = new BehaviorSubject('');
  firstSubtitle = new BehaviorSubject('');
  firstTranslate = new BehaviorSubject(false);
  secondTranslate = new BehaviorSubject(false);
  firstImage: Observable<ImageData> = of({ imageUrl: undefined });

  // SECOND IMAGE
  secondTitle = new BehaviorSubject('');
  secondSubtitle = new BehaviorSubject('');
  secondImage: Observable<ImageData> = of({ imageUrl: undefined });

  firstVisible = new BehaviorSubject(true);
  alive = true;
  showImage = true;
  uploadPolicy$!: Observable<UploadPolicy | undefined>;
  subscription!: Subscription;

  constructor(public route: ActivatedRoute, public images: ImagesService, private store: Store) {}

  ngOnInit() {
    this.uploadPolicy$ = this.store.pipe(select(getUploadPolicy));
    this.subscription = this.uploadPolicy$.subscribe((data) => {
      if (data?.hoa_docs) {
        this.showImage = false;
      }
    });
    this.route.data
      .pipe(
        takeWhile(() => this.alive),
        map((data) => data.imageData),
        // allows us to use a single Observable; or Selector Observable, wrapped in another Observable (ex. /apps/prequalification/src/app/apply/guards/ImageData.resolver.ts)
        map((data) => (data instanceof Observable ? data : of(data))),
        switchMap((data) => data),
        filter((data: ImageMeta) => Boolean(data && data.image)),
        distinctUntilKeyChanged('image'),
        map((data: ImageMeta, index) => ({ data, index }))
      )
      .subscribe(this.setDetails.bind(this));
  }

  ngOnDestroy() {
    this.alive = false;
    this.subscription.unsubscribe();
  }

  setDetails({ data, index }) {
    if (index % 2 === 0) {
      this.firstTitle.next(data.title);
      this.firstSubtitle.next(data.subtitle);
      this.firstTranslate.next(data.translate);
      this.firstImage = this.images.get$(data.image + 'Desktop');
    } else {
      this.secondTitle.next(data.title);
      this.secondSubtitle.next(data.subtitle);
      this.secondTranslate.next(data.translate);
      this.secondImage = this.images.get$(data.image + 'Desktop');
    }

    // gives time for the ngIf to resolve correctly
    // and then animate the opacity change
    animationFrameScheduler.schedule(() => this.firstVisible.next(index % 2 === 0));
  }
}
